import React, { useMemo } from 'react';
import { EventType, ICalendarEvent } from 'model/calendar/events';
import {
  getSyncMessages,
  DOWNSTREAM_SYNC_STATUS,
  PENDING_CANCEL_AXON_TOOLTIP,
  PENDING_CANCEL_ATHENA_TOOLTIP
} from 'utils/constants/planner';
import { formatEventPopupTime, formatHhMmTime } from 'utils/format';
import { isFullySyncedEvent } from 'utils/validators/dragEventValidator';
import { ItemTooltipWrapper } from './style';
import { isCancelledEvent } from 'utils/validators/BulkCancel';
import { EVENT_TYPES } from 'utils/constants/appointmentsTypes';
import { Appointment_Types } from 'components/AvailabilityTemplateModalForm/EditForm/dropdownOptions';
import {
  isSeriesEndingWithin4Weeks,
  formatSeriesEndDate
} from 'utils/validators/planner';

interface Props {
  itemRenderer: any;
}
const ItemTooltip: React.FC<Props> = ({ itemRenderer }: Props) => {
  const { item }: { item: ICalendarEvent } = itemRenderer;

  console.log('💬 [ItemTooltip] Rendering tooltip with:', {
    eventId: item.id,
    'item.crStatus': item.crStatus,
    'item.athenaStatus': item.athenaStatus,
    isFullySynced: isFullySyncedEvent(item.athenaStatus, item.crStatus)
  });

  const fullData = useMemo(
    () => ({
      subtype: item.appointmentType?.title,
      parentType: item.appointmentType?.parent?.title,
      room: item.room?.name || '',
      duration: `${formatHhMmTime(item.start)} - ${formatEventPopupTime(
        item.end
      )} `
    }),
    [item]
  );

  const filteredAvailabilities = Appointment_Types.filter(appointmentType => {
    if (fullData.subtype === appointmentType.value) {
      return true;
    }
    return false;
  });

  let abbreviationArray: any = [];
  let abreviationString: any;

  if (filteredAvailabilities.length != 0) {
    item.clinics?.map((clinic: any) => {
      abbreviationArray.push(clinic?.abbreviation);
    });
  }

  abreviationString = abbreviationArray.join(' | ');

  const getAppointmnetTitle = useMemo(() => {
    const appointmentTitle = fullData.parentType
      ? `${fullData.parentType}${'(' + fullData.subtype + ')'}`
      : `${fullData.subtype}`;
    if (appointmentTitle == undefined) {
      return '';
    } else {
      return appointmentTitle;
    }
  }, [fullData]);

  const clinicTimezone = useMemo(() => {
    return (
      item.clinic?.timezone ||
      item.client?.clinic?.timezone ||
      item.provider?.clinic?.timezone
    );
  }, [
    item.clinic?.timezone,
    item.client?.clinic?.timezone,
    item.provider?.clinic?.timezone
  ]);

  const isEndingSoon = useMemo(() => {
    return isSeriesEndingWithin4Weeks(
      item.recurrencePattern?.recurringUntil,
      item.recurrencePattern?.recurringEvery,
      clinicTimezone
    );
  }, [
    item.recurrencePattern?.recurringUntil,
    item.recurrencePattern?.recurringEvery,
    clinicTimezone
  ]);

  const seriesEndDate = useMemo(() => {
    if (!isEndingSoon) return '';
    return formatSeriesEndDate(
      item.recurrencePattern?.recurringUntil,
      clinicTimezone
    );
  }, [isEndingSoon, item.recurrencePattern?.recurringUntil, clinicTimezone]);

  const isInvisibleAppointment = useMemo(() => {
    return item.isPendingConfirmation;
  }, [item.isPendingConfirmation]);

  const eventTypeName = item.appointmentType?.eventType?.name;
  const syncsToAthena =
    eventTypeName === EVENT_TYPES.MED ||
    eventTypeName === EVENT_TYPES.Counseling;
  const athenaCancelSucceeded =
    item.athenaStatus === DOWNSTREAM_SYNC_STATUS.CANCEL_SUCCESS;
  const athenaCancelFailed =
    item.athenaStatus === DOWNSTREAM_SYNC_STATUS.CANCEL_FAILURE;
  const cancelInProgressMessage =
    !syncsToAthena || athenaCancelSucceeded
      ? PENDING_CANCEL_AXON_TOOLTIP
      : PENDING_CANCEL_ATHENA_TOOLTIP;
  const showCancelInProgress = !!item.isCancelInProgress && !athenaCancelFailed;

  return (
    <ItemTooltipWrapper>
      {item.type === EventType.clientEvent ||
      item.type === EventType.phantomClientEvent ||
      item.type === EventType.phantomObserverEvent ? (
        <>
          {isInvisibleAppointment && (
            <p className="invisible-label">Invisible Appointment</p>
          )}
          {isEndingSoon && seriesEndDate && (
            <p className="series-ending">Series Ending on {seriesEndDate}</p>
          )}
          {item.title?.trim() && (
            <p>{`${item.title || ''}${item.clinic?.id &&
              `(${item.clinic?.abbreviation || ''})`}`}</p>
          )}
          <p>
            {getAppointmnetTitle},{fullData.duration || ''}
            {/* {fullData.room || ''} */}
          </p>
        </>
      ) : (
        <>
          {isInvisibleAppointment && (
            <p className="invisible-label">Invisible Appointment</p>
          )}
          {isEndingSoon && seriesEndDate && (
            <p className="series-ending">Series Ending on {seriesEndDate}</p>
          )}
          <p>{item.title}</p>
          <p className="clinic-abbr">{abreviationString}</p>
          <p>
            {getAppointmnetTitle},{fullData.duration || ' '}
          </p>
        </>
      )}
      {showCancelInProgress ? (
        // Optimistic cancel feedback: shown immediately on cancel, before the
        // event falls off the calendar. Read directly from the flag because the
        // tile overrides crStatus/athenaStatus with its useEventSyncStatus values.
        // Message is type-aware (Athena-first for MED/Counseling, else Axon).
        <text className="error">
          <p>{cancelInProgressMessage}</p>
        </text>
      ) : (
        !isFullySyncedEvent(
          item.athenaStatus,
          item.crStatus,
          item.appointmentType?.eventType?.name
        ) &&
        !isCancelledEvent(item) && (
          <text className="error">
            {getSyncMessages(
              item.crStatus!,
              item.athenaStatus!,
              item.appointmentType?.eventType?.name
            ).map(str => (
              <p>{str}</p>
            ))}
          </text>
        )
      )}
    </ItemTooltipWrapper>
  );
};
export default ItemTooltip;
